
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: (e?: React.MouseEvent) => void;
  isDark: boolean;
  isTransitioning: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('roonchain-theme');
    return (saved as Theme) || 'light';
  });
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    localStorage.setItem('roonchain-theme', theme);
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const createRipple = useCallback((x: number, y: number, isDarkMode: boolean) => {
    const ripple = document.createElement('div');
    ripple.className = 'theme-ripple';
    
    // 计算需要覆盖整个屏幕的尺寸
    const maxDimension = Math.max(window.innerWidth, window.innerHeight) * 2.5;
    
    ripple.style.width = `${maxDimension}px`;
    ripple.style.height = `${maxDimension}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.style.background = isDarkMode 
      ? 'radial-gradient(circle, rgba(13, 17, 23, 0.95) 0%, rgba(13, 17, 23, 0.8) 50%, transparent 70%)'
      : 'radial-gradient(circle, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.8) 50%, transparent 70%)';
    
    document.body.appendChild(ripple);
    
    setTimeout(() => {
      ripple.remove();
    }, 800);
  }, []);

  const toggleTheme = useCallback((e?: React.MouseEvent) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    
    // 获取点击位置，如果没有事件则使用屏幕中心
    const x = e?.clientX ?? window.innerWidth / 2;
    const y = e?.clientY ?? window.innerHeight / 2;
    
    const newTheme = theme === 'light' ? 'dark' : 'light';
    
    // 创建涟漪效果
    createRipple(x, y, newTheme === 'dark');
    
    // 延迟切换主题，让涟漪动画先展开一点
    setTimeout(() => {
      setTheme(newTheme);
      
      // 给页面内容添加淡入效果
      document.body.classList.add('theme-fade');
      setTimeout(() => {
        document.body.classList.remove('theme-fade');
      }, 500);
    }, 150);
    
    // 过渡完成后重置状态
    setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  }, [theme, isTransitioning, createRipple]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark: theme === 'dark', isTransitioning }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
